<template>
  <div class="font-picker">
    <font-select
      :value="selectedValue"
      :custom-font="customFont"
      @select="onSelect"
      @custom-font="onCustomFont"
    />

    <div v-for="(override, index) in languageOverrides" :key="`lang-font-${index}`" class="lang-font-row mb-3 mt-3">
      <div class="d-flex align-start">
        <v-select
          :value="override.lang"
          @change="updateOverrideLang(index, $event)"
          :items="availableOverrideLanguages(override.lang)"
          item-text="text"
          item-value="value"
          solo
          hide-details
          class="language-select mr-3"
        ></v-select>

        <font-select
          :value="override.fontFamily"
          :custom-font="languageCustomFonts[override.lang]"
          class="mr-3"
          @select="onLanguageSelect(override.lang, $event)"
          @custom-font="onLanguageCustomFont(override.lang, $event)"
        />

        <v-btn icon class="mt-2" @click="removeOverride(index)">
          <v-icon>$remove</v-icon>
        </v-btn>
      </div>
    </div>

    <v-btn text color="primary" class="ml-0 mt-2" @click="addOverride" :disabled="addableLanguages.length === 0">
      <v-icon left>mdi-plus</v-icon>
      {{$t('design_font_language_add')}}
    </v-btn>
  </div>
</template>

<script>
import { ALL_FONTS, loadGoogleFont } from '@/utils/fontRegistry'
import FontSelect from '@/components/FontSelect'
import languages from '@/utils/languages'

export default {
  name: 'FontPicker',
  components: { FontSelect },
  props: {
    value: {
      type: Object,
      default: null
    },
    languageFonts: {
      type: Object,
      default: () => ({})
    },
    availableLanguages: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      customFont: null,
      languageCustomFonts: {}
    }
  },
  computed: {
    selectedValue () {
      if (!this.value) return this.$t('design_font_default')
      const match = ALL_FONTS.find(f => f.family === this.value.family)
      if (match) return match.family
      return this.value.family || this.$t('custom')
    },

    languageOverrides () {
      return Object.keys(this.languageFonts).filter(lang => !!lang).map(lang => {
        const font = this.languageFonts[lang]
        const isDefault = font && font.source === 'default' && !font.family
        return {
          lang,
          fontFamily: isDefault ? this.$t('design_font_default') : (font ? font.family : null),
          font: font
        }
      })
    },

    addableLanguages () {
      const usedLangs = Object.keys(this.languageFonts)
      return this.availableLanguages.filter(l => !usedLangs.includes(l))
    }
  },
  methods: {
    onSelect (font) {
      this.$emit('update:value', font)
    },
    onCustomFont (font) {
      this.customFont = font
      this.$emit('update:value', font)
    },

    langName (code) {
      const found = languages.list.find(l => l.key === code)
      return found ? found.name : code
    },

    availableOverrideLanguages (currentLang) {
      const usedLangs = Object.keys(this.languageFonts).filter(l => l !== currentLang)
      return this.availableLanguages
        .filter(l => !usedLangs.includes(l))
        .map(l => ({ text: this.langName(l), value: l }))
    },

    addOverride () {
      const available = this.addableLanguages
      if (available.length === 0) return
      this.$emit('update:language-font', { lang: available[0], font: { source: 'default' } })
    },

    removeOverride (index) {
      const override = this.languageOverrides[index]
      if (override) {
        this.$emit('update:language-font', { lang: override.lang, font: null })
      }
    },

    updateOverrideLang (index, newLang) {
      const override = this.languageOverrides[index]
      if (!override) return
      const customFont = this.languageCustomFonts[override.lang]
      if (customFont) {
        this.$delete(this.languageCustomFonts, override.lang)
        this.$set(this.languageCustomFonts, newLang, customFont)
      }
      this.$emit('update:language-font', { lang: override.lang, font: null })
      this.$emit('update:language-font', { lang: newLang, font: override.font })
    },

    onLanguageSelect (lang, font) {
      this.$emit('update:language-font', { lang, font: font || { source: 'default' } })
    },

    onLanguageCustomFont (lang, font) {
      this.$set(this.languageCustomFonts, lang, font)
      this.$emit('update:language-font', { lang, font })
    }
  },
  created () {
    if (this.value && !ALL_FONTS.find(f => f.family === this.value.family)) {
      this.customFont = { ...this.value }
      loadGoogleFont(this.customFont.cssUrl).catch(() => {})
    }
    Object.keys(this.languageFonts).forEach(lang => {
      const font = this.languageFonts[lang]
      if (font && font.family && !ALL_FONTS.find(f => f.family === font.family)) {
        this.$set(this.languageCustomFonts, lang, { ...font })
        loadGoogleFont(font.cssUrl).catch(() => {})
      }
    })
  }
}
</script>

<style lang="sass" scoped>
.language-select
  max-width: 220px
</style>
